import React from "react";
import { useTheme } from "@mui/material";
import DSBox from "../../system-design/DSBox";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import DSTypography from "../../system-design/DSTypography";
import DSAvatar from "../../system-design/DSAvatar";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CakeIcon from "@mui/icons-material/Cake";
import InfoIcon from "@mui/icons-material/Info";
import StarIcon from "@mui/icons-material/Star";
import { User } from "../../../api/admin";
import ReviewSocialLinks from "../../../components/Widgets/ReviewSocialLinks";

interface CreatorCardProps {
  creator: User | undefined;
}

const CreatorCard: React.FC<CreatorCardProps> = ({ creator }) => {
  const { spacing, palette } = useTheme();

  return (
    <DSCard
      sx={{
        flex: 1,
        padding: spacing(3),
        borderRadius: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        border: "1px solid",
        borderColor: palette.divider,
        backgroundColor: palette.background.paper,
      }}
    >
      <DSCardContent sx={{ padding: 0 }}>
        {!creator ? (
          <DSBox
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: spacing(4),
              textAlign: "center",
            }}
          >
            <PersonIcon
              sx={{
                fontSize: 48,
                color: palette.text.disabled,
                marginBottom: spacing(2),
              }}
            />
            <DSTypography
              variant="h6"
              sx={{
                color: palette.text.secondary,
                marginBottom: spacing(1),
              }}
            >
              No Creator Selected
            </DSTypography>
            <DSTypography
              variant="body2"
              sx={{
                color: palette.text.disabled,
              }}
            >
              Please select a creator from the dropdown above
            </DSTypography>
          </DSBox>
        ) : (
          <DSBox>
            <DSBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: spacing(3),
                marginBottom: spacing(3),
                paddingBottom: spacing(2),
                borderBottom: "1px solid " + palette.divider,
              }}
            >
              <DSBox sx={{ position: "relative" }}>
                <DSAvatar
                  src={creator?.photoUrl || ""}
                  alt={creator?.name}
                  sx={{
                    width: 80,
                    height: 80,
                    border: "3px solid",
                    borderColor: palette.primary.main + "20",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
                />
                {creator?.featured && (
                  <StarIcon
                    sx={{
                      position: "absolute",
                      top: -4,
                      right: -4,
                      fontSize: 20,
                      color: "#FFD700",
                      backgroundColor: palette.background.paper,
                      borderRadius: "50%",
                      padding: "2px",
                    }}
                  />
                )}
              </DSBox>
              <DSBox sx={{ flex: 1 }}>
                <DSBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing(1),
                    marginBottom: spacing(1),
                  }}
                >
                  <DSTypography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: palette.text.primary,
                    }}
                  >
                    {creator?.name}
                  </DSTypography>
                </DSBox>
                <DSBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing(1),
                  }}
                >
                  <EmailIcon
                    sx={{
                      fontSize: 16,
                      color: palette.text.secondary,
                    }}
                  />
                  <DSTypography
                    variant="body2"
                    sx={{
                      color: palette.text.secondary,
                    }}
                  >
                    {creator?.email}
                  </DSTypography>
                </DSBox>
              </DSBox>
            </DSBox>

            <DSBox
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: spacing(2),
                marginBottom: spacing(3),
              }}
            >
              {creator?.country && (
                <DSBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing(1),
                  }}
                >
                  <LocationOnIcon
                    sx={{
                      fontSize: 18,
                      color: palette.text.secondary,
                    }}
                  />
                  <DSBox>
                    <DSTypography
                      variant="caption"
                      sx={{
                        color: palette.text.secondary,
                        display: "block",
                        lineHeight: 1,
                      }}
                    >
                      Country
                    </DSTypography>
                    <DSTypography
                      variant="body2"
                      sx={{
                        color: palette.text.primary,
                        fontWeight: 500,
                      }}
                    >
                      {creator.country}
                    </DSTypography>
                  </DSBox>
                </DSBox>
              )}

              {creator?.birthday && (
                <DSBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing(1),
                  }}
                >
                  <CakeIcon
                    sx={{
                      fontSize: 18,
                      color: palette.text.secondary,
                    }}
                  />
                  <DSBox>
                    <DSTypography
                      variant="caption"
                      sx={{
                        color: palette.text.secondary,
                        display: "block",
                        lineHeight: 1,
                      }}
                    >
                      Birthday
                    </DSTypography>
                    <DSTypography
                      variant="body2"
                      sx={{
                        color: palette.text.primary,
                        fontWeight: 500,
                      }}
                    >
                      {new Date(creator.birthday).toLocaleDateString()}
                    </DSTypography>
                  </DSBox>
                </DSBox>
              )}

              {creator?.state && (
                <DSBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: spacing(1),
                  }}
                >
                  <InfoIcon
                    sx={{
                      fontSize: 18,
                      color: palette.text.secondary,
                    }}
                  />
                  <DSBox>
                    <DSTypography
                      variant="caption"
                      sx={{
                        color: palette.text.secondary,
                        display: "block",
                        lineHeight: 1,
                      }}
                    >
                      Status
                    </DSTypography>
                    <DSTypography
                      variant="body2"
                      sx={{
                        color: palette.text.primary,
                        fontWeight: 500,
                        textTransform: "capitalize",
                      }}
                    >
                      {creator.state}
                    </DSTypography>
                  </DSBox>
                </DSBox>
              )}
            </DSBox>

            {(creator.bio || creator.description) && (
              <DSBox sx={{ marginBottom: spacing(3) }}>
                <DSTypography
                  variant="subtitle2"
                  sx={{
                    color: palette.text.primary,
                    fontWeight: 600,
                    marginBottom: spacing(1),
                  }}
                >
                  About
                </DSTypography>
                <DSTypography
                  variant="body2"
                  sx={{
                    color: palette.text.secondary,
                    lineHeight: 1.6,
                    backgroundColor: palette.action.hover,
                    padding: spacing(2),
                    borderRadius: 1,
                  }}
                >
                  {creator.bio || creator.description}
                </DSTypography>
              </DSBox>
            )}

            <ReviewSocialLinks links={creator.socialMedia} />
          </DSBox>
        )}
      </DSCardContent>
    </DSCard>
  );
};

export default CreatorCard;
